// src/components/CelestialScene.jsx
import { useEffect, useRef } from "react";
import * as THREE from "three";

const CelestialRecovery = () => {
  const canvasRef = useRef();
  const mountedRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || mountedRef.current) return;
    
    mountedRef.current = true;

    // Set up the scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#05091B");

    // Camera
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 5;

    // Renderer with error handling
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ 
        canvas: canvas, 
        alpha: true,
        preserveDrawingBuffer: false,
        powerPreference: "default"
      });
      renderer.setSize(window.innerWidth, window.innerHeight);
    } catch (error) {
      console.warn('WebGL not available, skipping 3D scene:', error);
      mountedRef.current = false;
      return;
    }

    // Animation frame ID for cleanup
    let animationFrameId;

    // Stars
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 1000;
    const starPositions = [];
    for (let i = 0; i < starCount; i++) {
      starPositions.push((Math.random() - 0.5) * 200);
      starPositions.push((Math.random() - 0.5) * 200);
      starPositions.push((Math.random() - 0.5) * 200);
    }
    starGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starPositions, 3));
    const starMaterial = new THREE.PointsMaterial({ color: 0xffffff, size: 0.7 });
    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);

    // Indigo Nebula (rotating plane) - repositioned to avoid content overlap
    const nebulaGeometry = new THREE.PlaneGeometry(60, 26, 24);
    const nebulaMaterial = new THREE.MeshBasicMaterial({
      color: 0x4B0082, // Indigo
      transparent: true,
      opacity: 0.16, // Reduced opacity
      side: THREE.DoubleSide,
    });
    const nebula = new THREE.Mesh(nebulaGeometry, nebulaMaterial);
    nebula.position.set(-20, 10, -50); // Moved at center top
    nebula.rotation.z = Math.PI / 6;
    scene.add(nebula);

    // Moonlight reflection shimmer (simple light)
    const moonLight = new THREE.PointLight(0xaaaaff, 1, 100);
    moonLight.position.set(0, 10, 10);
    scene.add(moonLight);

    // Animate
    const animate = () => {
      if (!mountedRef.current) return;
      
      animationFrameId = requestAnimationFrame(animate);
      stars.rotation.y += 0.0008;
      stars.rotation.x += 0.0004;
      nebula.rotation.z += 0.0002;
      
      try {
        renderer.render(scene, camera);
      } catch (error) {
        console.warn('Render failed, stopping animation:', error);
        return;
      }
    };
    animate();

    // Resize handling
    const handleResize = () => {
      if (!mountedRef.current) return;
      
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup function
    return () => {
      mountedRef.current = false;
      
      // Cancel animation frame
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      // Remove event listener
      window.removeEventListener("resize", handleResize);
      
      // Dispose of Three.js objects to free memory
      if (starGeometry) starGeometry.dispose();
      if (starMaterial) starMaterial.dispose();
      if (nebulaGeometry) nebulaGeometry.dispose();
      if (nebulaMaterial) nebulaMaterial.dispose();
      if (scene && stars) scene.remove(stars);
      if (scene && nebula) scene.remove(nebula);
      if (scene && moonLight) scene.remove(moonLight);
      
      // Dispose renderer carefully - don't force context loss
      if (renderer) {
        try {
          renderer.dispose();
          // Don't force context loss as it can cause issues
        } catch (error) {
          console.warn('Error disposing renderer:', error);
        }
      }
      
      // Clear scene
      if (scene) scene.clear();
    };
  }, []);

  return <canvas ref={canvasRef} id="celestialCanvas" className="absolute inset-0 z-0" />;
};

export default CelestialRecovery;
